import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useEnterpriseHierarchyEnabled, useTenant } from '@/contexts/TenantContext';

export type OrganisationRole =
  | 'company_admin'
  | 'branch_admin'
  | 'unit_admin'
  | 'unit_manager'
  | 'user';

export interface OrganisationAssignment {
  id: string;
  role: OrganisationRole;
  branch_id: string | null;
  branch_name: string | null;
  unit_id: string | null;
  unit_name: string | null;
  is_primary: boolean;
  status: 'active' | 'inactive';
}

export interface OrganisationUnit {
  id: string;
  tenant_id: string;
  branch_id: string;
  branch_name: string;
  name: string;
  code: string | null;
  description: string | null;
  is_active: boolean;
}

interface OrganisationContextPayload {
  profile_id: string;
  tenant_id: string;
  global_company_name: string | null;
  last_selected_unit_id: string | null;
  assignments: OrganisationAssignment[];
}

interface OrganisationContextValue {
  hasEnterpriseHierarchy: boolean;
  assignments: OrganisationAssignment[];
  units: OrganisationUnit[];
  activeUnitId: string | null;
  activeUnit: OrganisationUnit | null;
  activeAssignment: OrganisationAssignment | null;
  globalCompanyName: string | null;
  effectiveRole: OrganisationRole | null;
  isLoading: boolean;
  isCompanyAdmin: boolean;
  isBranchAdmin: boolean;
  isUnitAdmin: boolean;
  isUnitManager: boolean;
  canManageOrganisation: boolean;
  canManagePeople: boolean;
  canCreateAssets: boolean;
  setActiveUnit: (unitId: string) => Promise<void>;
  refreshOrganisation: () => Promise<void>;
}

const OrganisationContext = createContext<OrganisationContextValue | undefined>(undefined);
const enterpriseDb = supabase as any;

const ROLE_PRIORITY: OrganisationRole[] = [
  'company_admin',
  'branch_admin',
  'unit_admin',
  'unit_manager',
  'user',
];

function getHighestRole(assignments: OrganisationAssignment[]): OrganisationRole | null {
  return ROLE_PRIORITY.find((role) => assignments.some((assignment) => assignment.role === role)) || null;
}

export function OrganisationProvider({ children }: { children: ReactNode }) {
  const { user, profile } = useAuth();
  const { tenant } = useTenant();
  const enterpriseHierarchyEnabled = useEnterpriseHierarchyEnabled();
  const queryClient = useQueryClient();
  const tenantId = tenant?.tenant_id || user?.tenant_id || null;
  const profileId = profile?.id || user?.id || null;
  const storageKey = tenantId && profileId ? `medd-active-unit:${tenantId}:${profileId}` : null;
  const [activeUnitId, setActiveUnitId] = useState<string | null>(null);

  const contextQuery = useQuery({
    queryKey: ['organisation-context', tenantId, profileId],
    queryFn: async (): Promise<OrganisationContextPayload | null> => {
      const { data, error } = await enterpriseDb.rpc('get_my_organisation_context');
      if (error) {
        // Feature-disabled or older projects may not have this enterprise RPC.
        if (error.code === 'PGRST202' || error.code === '42883') return null;
        throw error;
      }
      return data as OrganisationContextPayload;
    },
    enabled: Boolean(user && tenantId && enterpriseHierarchyEnabled),
    retry: false,
  });

  const assignments = contextQuery.data?.assignments || [];
  const isCompanyAdmin = assignments.some((assignment) => assignment.role === 'company_admin');

  const unitsQuery = useQuery({
    queryKey: ['organisation-units', tenantId, profileId],
    queryFn: async (): Promise<OrganisationUnit[]> => {
      const [unitsResult, branchesResult] = await Promise.all([
        enterpriseDb
          .from('organisation_units')
          .select('id, tenant_id, branch_id, name, code, description, is_active')
          .eq('tenant_id', tenantId)
          .eq('is_active', true)
          .order('name'),
        enterpriseDb
          .from('companies')
          .select('id, name')
          .eq('tenant_id', tenantId)
          .eq('is_active', true),
      ]);
      if (unitsResult.error) throw unitsResult.error;
      if (branchesResult.error) throw branchesResult.error;

      const branchNames = new Map<string, string>(
        (branchesResult.data || []).map((branch: { id: string; name: string }) => [branch.id, branch.name]),
      );

      return (unitsResult.data || [])
        .map((unit: Omit<OrganisationUnit, 'branch_name'>) => ({
          ...unit,
          branch_name: branchNames.get(unit.branch_id) || 'Unassigned branch',
        }))
        .sort((left: OrganisationUnit, right: OrganisationUnit) => (
          left.branch_name.localeCompare(right.branch_name)
          || left.name.localeCompare(right.name)
        ));
    },
    enabled: Boolean(contextQuery.data && tenantId),
  });

  const units = unitsQuery.data || [];

  useEffect(() => {
    if (!contextQuery.data || !units.length) {
      setActiveUnitId(null);
      return;
    }

    const savedUnitId = storageKey ? window.localStorage.getItem(storageKey) : null;
    const availableUnitIds = new Set(units.map((unit) => unit.id));
    const primaryUnitId = assignments.find((assignment) => assignment.is_primary && assignment.unit_id)?.unit_id;
    const preferredUnitId = [
      contextQuery.data.last_selected_unit_id,
      savedUnitId,
      primaryUnitId,
      units[0]?.id,
    ].find((unitId): unitId is string => Boolean(unitId && availableUnitIds.has(unitId)));

    setActiveUnitId(preferredUnitId || null);
  }, [assignments, contextQuery.data, storageKey, units]);

  const setActiveUnit = useCallback(async (unitId: string) => {
    if (!units.some((unit) => unit.id === unitId)) {
      throw new Error('That unit is outside your organisation scope.');
    }

    const { error } = await enterpriseDb.rpc('set_my_active_unit', { p_unit_id: unitId });
    if (error) throw error;

    setActiveUnitId(unitId);
    if (storageKey) window.localStorage.setItem(storageKey, unitId);
    await queryClient.invalidateQueries({ queryKey: ['organisation-context', tenantId, profileId] });
  }, [profileId, queryClient, storageKey, tenantId, units]);

  const activeUnit = useMemo(
    () => units.find((unit) => unit.id === activeUnitId) || null,
    [activeUnitId, units],
  );
  const activeScopeAssignments = useMemo(() => {
    if (!activeUnit) return assignments;
    return assignments.filter((assignment) => (
      assignment.branch_id === activeUnit.branch_id
      && (assignment.unit_id === null || assignment.unit_id === activeUnit.id)
    ));
  }, [activeUnit, assignments]);
  const activeAssignment = useMemo(() => {
    if (isCompanyAdmin) {
      return assignments.find((assignment) => assignment.role === 'company_admin') || null;
    }
    return activeScopeAssignments.find((assignment) => assignment.unit_id === activeUnitId)
      || activeScopeAssignments.find((assignment) => assignment.unit_id === null)
      || assignments.find((assignment) => assignment.is_primary)
      || assignments[0]
      || null;
  }, [activeScopeAssignments, activeUnitId, assignments, isCompanyAdmin]);
  const effectiveRole = isCompanyAdmin ? 'company_admin' : getHighestRole(activeScopeAssignments);
  const globalCompanyName = contextQuery.data?.global_company_name || null;
  const isBranchAdmin = effectiveRole === 'branch_admin';
  const isUnitAdmin = effectiveRole === 'unit_admin';
  const isUnitManager = effectiveRole === 'unit_manager';

  const refreshOrganisation = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['organisation-context', tenantId, profileId] }),
      queryClient.invalidateQueries({ queryKey: ['organisation-units', tenantId, profileId] }),
    ]);
  }, [profileId, queryClient, tenantId]);

  const value = useMemo<OrganisationContextValue>(() => ({
    hasEnterpriseHierarchy: enterpriseHierarchyEnabled && Boolean(contextQuery.data),
    assignments,
    units,
    activeUnitId,
    activeUnit,
    activeAssignment,
    globalCompanyName,
    effectiveRole,
    isLoading: contextQuery.isLoading || unitsQuery.isLoading,
    isCompanyAdmin,
    isBranchAdmin,
    isUnitAdmin,
    isUnitManager,
    canManageOrganisation: isCompanyAdmin || isBranchAdmin || isUnitAdmin || isUnitManager,
    canManagePeople: isCompanyAdmin || isBranchAdmin || isUnitAdmin || isUnitManager,
    canCreateAssets: isCompanyAdmin || isBranchAdmin || isUnitAdmin || isUnitManager,
    setActiveUnit,
    refreshOrganisation,
  }), [
    activeAssignment,
    activeUnit,
    activeUnitId,
    assignments,
    contextQuery.isLoading,
    contextQuery.data,
    globalCompanyName,
    effectiveRole,
    isBranchAdmin,
    isCompanyAdmin,
    isUnitAdmin,
    isUnitManager,
    refreshOrganisation,
    setActiveUnit,
    units,
    unitsQuery.isLoading,
  ]);

  return <OrganisationContext.Provider value={value}>{children}</OrganisationContext.Provider>;
}

export function useOrganisation() {
  const context = useContext(OrganisationContext);
  if (!context) throw new Error('useOrganisation must be used inside OrganisationProvider');
  return context;
}

export const ORGANISATION_ROLE_LABELS: Record<OrganisationRole, string> = {
  company_admin: 'Company Administrator',
  branch_admin: 'Branch Administrator',
  unit_admin: 'Unit Administrator',
  unit_manager: 'Unit Manager',
  user: 'User',
};
